import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, FolderPlus, HardDrive, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import NotFound from "@/pages/NotFound";
import { useAdmin } from "@/context/AdminContext";
import { api, errText, mediaUrl } from "@/lib/api";
import { FolderIcon } from "@/components/files/FolderIcon";
import { date, iconFor, size } from "@/lib/files";

export default function ContentFilesPage({ embedded = false }) {
  const { user, isAdmin, canEdit, can } = useAdmin();
  const hasContentFilesAccess = isAdmin || canEdit || can("content_files");
  const [params, setParams] = useSearchParams();
  const folderId = params.get("folder") || "";
  const [data, setData] = useState({ folder: null, path: [], folders: [], files: [] });
  const [busy, setBusy] = useState(0);
  const input = useRef(null);

  const load = useCallback(() => {
    api.get("/content-files", { params: folderId ? { folder_id: folderId } : {} })
      .then((response) => setData(response.data))
      .catch((error) => { toast.error(errText(error)); if (folderId) setParams({}); });
  }, [folderId, setParams]);

  useEffect(() => {
    if (user && hasContentFilesAccess) load();
  }, [user, hasContentFilesAccess, load]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!hasContentFilesAccess) return <NotFound />;

  const open = (id) => setParams(id ? { folder: id } : {});
  const newFolder = async () => {
    const name = window.prompt("Folder name:", "New folder");
    if (!name?.trim()) return;
    try {
      const response = await api.post("/content-files/folders", { name: name.trim(), parent_id: folderId || null });
      open(response.data.id);
    } catch (error) {
      toast.error(errText(error));
    }
  };

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(list.length);
    for (const file of list) {
      const form = new FormData();
      form.append("file", file);
      if (folderId) form.append("folder_id", folderId);
      try {
        await api.post("/media", form);
      } catch (error) {
        toast.error(`${file.name}: ${errText(error, "Upload failed")}`);
      }
      setBusy((current) => current - 1);
    }
    load();
  };

  const empty = !data.folders.length && !data.files.length;
  return (
    <div className={embedded ? "" : "min-h-screen bg-[#FBF9F5]"}>
      <div className={embedded ? "" : "mx-auto max-w-7xl px-5 pb-28 pt-10 sm:px-8 lg:px-12"}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          {!embedded && <div>
            <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]">Videos and images</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold sm:text-5xl">Content Files</h1>
          </div>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={newFolder} className="flex items-center gap-2 border border-black/80 px-4 py-2.5 text-sm font-bold hover:bg-black hover:text-white"><FolderPlus className="h-4 w-4" />New folder</button>
            <button type="button" onClick={() => input.current?.click()} disabled={busy > 0} className="flex items-center gap-2 bg-[#0A0A0A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{busy ? `Uploading (${busy})…` : "Upload files"}
            </button>
            <input ref={input} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(event) => { upload(event.target.files); event.target.value = ""; }} />
          </div>
        </div>

        <section className="mt-8 border border-black/15 bg-white">
          <nav className="flex flex-wrap items-center gap-1 border-b border-black/10 bg-[#F3F2EF] px-4 py-2.5 text-sm" aria-label="Content file folders">
            <button type="button" onClick={() => open("")} className="flex items-center gap-1.5 rounded px-2 py-1 font-semibold hover:bg-black/10"><HardDrive className="h-4 w-4" />Content Files</button>
            {data.path.map((folder) => (
              <span key={folder.id} className="flex items-center gap-1"><ChevronRight className="h-4 w-4 text-[#57534E]" /><button type="button" onClick={() => open(folder.id)} className="rounded px-2 py-1 font-semibold hover:bg-black/10">{folder.name}</button></span>
            ))}
          </nav>

          <div className="min-h-[360px] p-4 sm:p-6">
            {data.folders.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
                {data.folders.map((folder) => (
                  <button key={folder.id} type="button" onClick={() => open(folder.id)} className="group flex min-w-0 flex-col items-center rounded p-2 text-center hover:bg-[#E8F0FE]">
                    <FolderIcon className="h-14 w-[4.5rem] transition-transform group-hover:-translate-y-0.5" />
                    <span className="mt-1 w-full break-words text-sm font-semibold">{folder.name}</span>
                    <span className="text-[11px] text-[#57534E]">{folder.count} {folder.count === 1 ? "item" : "items"}</span>
                  </button>
                ))}
              </div>
            )}

            {data.files.length > 0 && (
              <div className={data.folders.length ? "mt-6 overflow-x-auto" : "overflow-x-auto"}>
                <table className="w-full min-w-[360px] text-left text-sm">
                  <thead className="border-b border-black/10 text-xs uppercase tracking-wider text-[#57534E]">
                    <tr><th className="py-2 font-semibold">Name</th><th className="py-2 font-semibold">Files</th><th className="hidden py-2 font-semibold md:table-cell">Added</th><th className="hidden py-2 font-semibold sm:table-cell">Size</th></tr>
                  </thead>
                  <tbody>
                    {data.files.map((file) => {
                      const [Icon, color] = iconFor(file);
                      const preview = mediaUrl(file.id);
                      return (
                        <tr key={file.id} className="border-b border-black/5">
                          <td className="py-2.5"><span className="flex min-w-0 items-center gap-2 font-semibold"><Icon className={`h-5 w-5 shrink-0 ${color}`} /><a href={preview} className="min-w-0 break-words hover:underline">{file.original_filename || file.name || "Untitled file"}</a></span></td>
                          <td className="py-2.5 text-xs text-[#57534E]">{file.kind === "video" ? "Video" : "Image"}</td>
                          <td className="hidden whitespace-nowrap py-2.5 text-[#57534E] md:table-cell">{date(file.created_at)}</td>
                          <td className="hidden whitespace-nowrap py-2.5 text-[#57534E] sm:table-cell">{size(file.size)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {empty && (
              <div className="flex min-h-[300px] flex-col items-center justify-center text-center text-[#57534E]">
                <FolderIcon className="h-20 w-24 opacity-60" />
                <p className="mt-4 text-base font-semibold text-[#0A0A0A]">This folder is empty</p>
                <p className="mt-1 text-sm">Create a folder or upload image and video files.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}